import React from "react";
import GDeskWorksCard from "../Card/GDeskWorksCard";
import GDeskDifferenceCard from "../Card/GDeskDifferenceCard";
import { FaSearch, FaHandshake, FaHeadset, FaChartLine } from "react-icons/fa";
import { motion } from "framer-motion";

const GDeskWork = () => {
  const workSteps = [
    {
      stepNumber: 1,
      title: "We Understand Your Needs",
      description:
        "We assess your workflows, tasks, and gaps to identify exactly what kind of support you need.",
      icon: <FaSearch />,
    },
    {
      stepNumber: 2,
      title: "We Match You with the Right VA",
      description:
        "You're paired with a trained virtual assistant in your specialization who already understands systems, SOPs, and structured workflows.",
      icon: <FaHandshake />,
    },
    {
      stepNumber: 3,
      title: "We Support Behind the Scenes",
      description:
        "Your VA isn't working alone. There's a system, tracking, and senior-level support ensuring quality and consistency.",
      icon: <FaHeadset />,
    },
    {
      stepNumber: 4,
      title: "You Focus on Growth",
      description:
        "Tasks get done properly, without constant supervision, so you can focus on scaling your business.",
      icon: <FaChartLine />,
    },
  ];

  const differentItems = [
    {
      id: 1,
      title: "Pre-Trained Virtual Assistants",
      description:
        "Our VAs undergo comprehensive training before being matched with clients. They already understand systems, SOPs, and structured workflows, so they can hit the ground running.",
    },
    {
      id: 2,
      title: "Systemized Support Structure",
      description:
        "We don't just assign a VA and disappear. Our team provides ongoing support, quality checks, and structured systems to ensure consistent delivery.",
    },
    {
      id: 3,
      title: "Reduced Mental Load",
      description:
        "Stop worrying about task completion. Our VAs take ownership of their work, giving you peace of mind and freeing up mental space for strategic decisions.",
    },
    {
      id: 4,
      title: "Team-Based Reliability",
      description:
        "If your primary VA is unavailable, our team steps in. You never have to worry about gaps in support or missed deadlines.",
    },
    {
      id: 5,
      title: "HIPAA-Compliant",
      description:
        "For medical practices, we ensure all VAs are trained in HIPAA compliance, protecting patient confidentiality and meeting regulatory requirements.",
    },
    {
      id: 6,
      title: "Filipino Workforce Advantage",
      description:
        "Our VAs are highly skilled English-speaking professionals who understand Western business culture, time zones, and communication styles.",
    },
    {
      id: 7,
      title: "Efficiency Over Cost Savings",
      description:
        "While we offer competitive rates, our focus is on efficiency and quality. We measure success by how much time and energy we save you, not just dollars.",
    },
  ];

  return (
    <div className="bg-gray-50 py-16 px-5 md:px-10">
      <div className="max-w-7xl mx-auto">
        {/* How GDesk Works Section - 4 columns */}
        <div className="text-center mb-12">
          <motion.h2
            className="text-3xl md:text-4xl font-bold text-primary"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            How GDesk Works?
          </motion.h2>
          <motion.p
            className="text-gray-600 mt-2"
            initial={{ opacity: 0, y: -40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            Simple 4-step process to get you started
          </motion.p>
        </div>

        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-20"
          initial={{ opacity: 0, y: -40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.8 }}
          viewport={{ once: true }}
        >
          {workSteps.map((step) => (
            <GDeskWorksCard
              key={step.stepNumber}
              stepNumber={step.stepNumber}
              title={step.title}
              description={step.description}
              icon={step.icon}
            />
          ))}
        </motion.div>

        {/* How GDesk Is Different Section - ONE COLUMN ONLY */}
        <div className="text-center mb-12">
          <motion.h2
            className="text-3xl md:text-4xl font-bold text-primary"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            How GDesk Is Different?
          </motion.h2>
          <motion.p
            className="text-gray-600 mt-2"
            initial={{ opacity: 0, y: -40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            Click on any item to learn more
          </motion.p>
        </div>

        {/* Single column layout - max-w-3xl centers the content */}
        <div className="max-w-3xl mx-auto">
          <div className="space-y-4">
            {differentItems.map((item) => (
              <GDeskDifferenceCard
                key={item.id}
                id={item.id}
                title={item.title}
                description={item.description}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GDeskWork;
